/**
 * Agent Detail (redesigned)
 *
 * Slim orchestrator: owns the top-level queries, mutations, and tab
 * routing, then delegates rendering to focused subcomponents under
 * `./detail/`. The old monolithic implementation lived here at ~1600
 * lines; this file is intentionally much smaller.
 */

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Paper, Tab, Tabs } from '@mui/material';
import {
  AutoAwesome as OverviewIcon,
  AccountTree as VisualizerIcon,
  Build as ToolIcon,
  WorkspacePremium as SkillIcon,
  History as HistoryIcon,
  Memory as MemoryIcon,
  SmartToy as AgentIcon,
  Tune as AdvancedIcon,
  AssignmentTurnedIn as ValidatorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { agentApi } from '../../services/api';
import { LoadingSpinner, ConfirmDialog } from '../common';
import { useAuth } from '../../context/AuthContext';
import PageBackButton from '../../../components/PageBackButton';

import { AgentSubAgentManager } from './AgentSubAgentManager';
import { AgentHeader } from './detail/AgentHeader';
import { AgentKpiStrip } from './detail/AgentKpiStrip';
import { OverviewTab } from './detail/OverviewTab';
import { AgentVisualizerCanvas } from './detail/AgentVisualizerCanvas';
import { ToolsTab } from './detail/ToolsTab';
import { SkillsTab } from './detail/SkillsTab';
import { MemoryTab } from './detail/MemoryTab';
import { ExecutionsTab } from './detail/ExecutionsTab';
import { AdvancedTab } from './detail/AdvancedTab';
import { ValidatorsTab } from './detail/ValidatorsTab';
import { AgentEditDialog, type AgentEditValues } from './detail/AgentEditDialog';
import { AgentSchedulePanel } from '../system/AgentSchedulePanel';
import {
  MemoryConfigDialog,
  type MemoryConfigValues,
} from './detail/MemoryConfigDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) =>
  value === index ? (
    <Box role="tabpanel" sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      {children}
    </Box>
  ) : null;

export const AgentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const agentId = parseInt(id || '0', 10);
  const isStaff = Boolean(user?.is_staff);

  const [activeTab, setActiveTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // ---------------------------------------------------------------- queries
  const { data: agent, isLoading } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => agentApi.get(agentId),
    enabled: !!agentId,
  });

  const { data: toolsData } = useQuery({
    queryKey: ['agent-tools', agentId],
    queryFn: () => agentApi.getTools(agentId),
    enabled: !!agentId,
  });

  const { data: executionsData } = useQuery({
    queryKey: ['agent-executions', agentId],
    queryFn: () => agentApi.getExecutions(agentId),
    enabled: !!agentId,
  });

  const { data: skillsData } = useQuery({
    queryKey: ['agent-skills', agentId],
    queryFn: () => agentApi.getSkills(agentId),
    enabled: !!agentId,
  });

  const { data: memoryStats } = useQuery({
    queryKey: ['agent-memory-stats', agentId],
    queryFn: () => agentApi.getMemoryStats(agentId),
    enabled: !!agentId,
  });

  // --------------------------------------------------------------- mutations
  const updateMutation = useMutation({
    mutationFn: (data: any) => agentApi.update(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agent-selector'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to update agent');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => agentApi.delete(agentId),
    onSuccess: () => {
      toast.success('Agent deleted');
      navigate('/ai/agents');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to delete agent');
    },
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      agentApi.exportAgent(agentId, { maskApiKeys: true, includeSubAgents: true }),
    onSuccess: (payload) => {
      try {
        const json = JSON.stringify(payload, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const safeName = (agent?.name || 'agent').replace(/[^a-z0-9-_]+/gi, '_');
        link.download = `${safeName}.agent.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Agent exported');
      } catch {
        toast.error('Failed to save export file');
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to export agent');
    },
  });

  const cloneMutation = useMutation({
    mutationFn: () => agentApi.cloneAgent(agentId),
    onSuccess: (cloned) => {
      toast.success(`Cloned as "${cloned.name}"`);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      if (cloned?.id) navigate(`/ai/agents/${cloned.id}`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to clone agent');
    },
  });

  // ------------------------------------------------------------------ derived
  const kpis = useMemo(() => {
    const assignments = toolsData?.assigned || agent?.tool_assignments || [];
    const enabled = assignments.filter((a: any) => a.is_enabled).length;
    const validatorAssignments = agent?.validator_assignments || [];
    const enabledValidators = validatorAssignments.filter((a: any) => a.is_enabled).length;
    const runs = executionsData?.results || [];
    const completed = runs.filter((r) => r.status === 'COMPLETED').length;
    const successRate = runs.length > 0 ? completed / runs.length : null;
    const lastRun = runs.length > 0 ? runs[0].started_at : null;
    return {
      toolCount: assignments.length,
      enabledToolCount: enabled,
      skillCount: skillsData?.assigned?.length || agent?.skill_assignments?.length || 0,
      validatorCount: validatorAssignments.length,
      enabledValidatorCount: enabledValidators,
      executionCount: executionsData?.count ?? runs.length,
      successRate,
      memoryCount: memoryStats?.total ?? 0,
      avgImportance: memoryStats?.avg_importance ?? null,
      lastRunAt: lastRun,
    };
  }, [toolsData, skillsData, agent, executionsData, memoryStats]);

  // ------------------------------------------------------------------ handlers
  const handleEditSubmit = (values: AgentEditValues) => {
    updateMutation.mutate(values, {
      onSuccess: () => {
        toast.success('Agent updated');
        setEditOpen(false);
      },
    });
  };

  const handleMemorySubmit = (values: MemoryConfigValues) => {
    if (!agent) return;
    const currentMemoryConfig = agent.memory_config || {};
    const currentSemanticSearch = currentMemoryConfig.semantic_search || {};
    const nextMemoryConfig = {
      ...currentMemoryConfig,
      enabled: values.memory_enabled,
      max_context_memories: values.max_context_memories,
      max_memories_per_type: values.max_memories_per_type,
      short_term_days: values.short_term_days,
      episodic_days: values.episodic_days,
      semantic_search: {
        ...currentSemanticSearch,
        enabled: values.enable_semantic_search,
      },
    };

    updateMutation.mutate(
      {
        model_id: agent.model?.id,
        system_prompt: agent.system_prompt,
        config: {
          ...(agent.config || {}),
          auto_summarize: values.auto_summarize,
          summarization_strategy: values.summarization_strategy,
          summarization_context_usage_threshold:
            values.summarization_context_usage_threshold,
          keep_recent_context_ratio: values.keep_recent_context_ratio,
          fallback_context_window_tokens: values.fallback_context_window_tokens,
          inject_context_usage: values.inject_context_usage,
          max_context_messages: values.max_context_messages,
          keep_recent_messages: values.keep_recent_messages,
          resummarize_interval: values.resummarize_interval,
          temperature: values.temperature,
          max_tokens: values.max_tokens,
          max_iterations: values.max_iterations,
          delegation_timeout_seconds: values.delegation_timeout_seconds,
          tool_result_max_chars: values.tool_result_max_chars,
          max_tool_context_messages: values.max_tool_context_messages,
          tool_memory_enabled: values.tool_memory_enabled,
          tool_memory_recent_items: values.tool_memory_recent_items,
          tool_memory_context_max_chars: values.tool_memory_context_max_chars,
          tool_memory_max_per_session: values.tool_memory_max_per_session,
          tool_memory_max_per_agent: values.tool_memory_max_per_agent,
        },
        memory_config: nextMemoryConfig,
      },
      {
        onSuccess: () => {
          toast.success('Memory settings saved');
          setMemoryOpen(false);
        },
      }
    );
  };

  const handleToggleActive = (next: boolean) => {
    if (!agent) return;
    updateMutation.mutate(
      {
        model_id: agent.model?.id,
        system_prompt: agent.system_prompt,
        is_active: next,
      },
      {
        onSuccess: () => {
          toast.success(next ? 'Agent activated' : 'Agent deactivated');
        },
      }
    );
  };

  // -------------------------------------------------------------------- render
  if (isLoading || !agent) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <AgentHeader
        agent={agent}
        isStaff={isStaff}
        onChat={() => navigate(`/ai/agents/${agentId}/chat`)}
        onEdit={() => setEditOpen(true)}
        onClone={() => cloneMutation.mutate()}
        onExport={() => exportMutation.mutate()}
        onDelete={() => setDeleteOpen(true)}
        onToggleActive={handleToggleActive}
        cloning={cloneMutation.isPending}
        exporting={exportMutation.isPending}
        toggling={updateMutation.isPending}
      />

      <AgentKpiStrip
        toolCount={kpis.toolCount}
        enabledToolCount={kpis.enabledToolCount}
        executionCount={kpis.executionCount}
        successRate={kpis.successRate}
        memoryCount={kpis.memoryCount}
        avgImportance={kpis.avgImportance}
        lastRunAt={kpis.lastRunAt}
      />

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: { xs: 1, sm: 2 },
            '& .MuiTab-root': {
              minHeight: 52,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.78rem', sm: '0.9rem' },
              minWidth: { xs: 'auto', sm: 120 },
            },
          }}
        >
          <Tab icon={<OverviewIcon fontSize="small" />} iconPosition="start" label="Overview" />
          <Tab icon={<VisualizerIcon fontSize="small" />} iconPosition="start" label="Visualiser" />
          <Tab
            icon={<ToolIcon fontSize="small" />}
            iconPosition="start"
            label={`Tools${kpis.toolCount ? ` · ${kpis.toolCount}` : ''}`}
          />
          <Tab
            icon={<SkillIcon fontSize="small" />}
            iconPosition="start"
            label={`Skills${kpis.skillCount ? ` · ${kpis.skillCount}` : ''}`}
          />
          <Tab icon={<AgentIcon fontSize="small" />} iconPosition="start" label="Sub-agents" />
          <Tab
            icon={<ValidatorIcon fontSize="small" />}
            iconPosition="start"
            label={`Validators${kpis.validatorCount ? ` (${kpis.enabledValidatorCount}/${kpis.validatorCount})` : ''}`}
          />
          <Tab icon={<ScheduleIcon fontSize="small" />} iconPosition="start" label="Schedules" />
          <Tab
            icon={<MemoryIcon fontSize="small" />}
            iconPosition="start"
            label={`Memory${kpis.memoryCount ? ` · ${kpis.memoryCount}` : ''}`}
          />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label={`Executions${kpis.executionCount ? ` · ${kpis.executionCount}` : ''}`}
          />
          <Tab icon={<AdvancedIcon fontSize="small" />} iconPosition="start" label="Advanced" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <OverviewTab
            agent={agent}
            saving={updateMutation.isPending}
            onOpenFullEdit={() => setEditOpen(true)}
            onSavePartial={async (partial) => {
              await updateMutation.mutateAsync({
                model_id: agent.model?.id,
                system_prompt: agent.system_prompt,
                ...partial,
              });
              toast.success('Agent updated');
            }}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <AgentVisualizerCanvas agent={agent} />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <ToolsTab agentId={agentId} />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <SkillsTab agentId={agentId} />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <AgentSubAgentManager agentId={agentId} agentName={agent.name} />
        </TabPanel>
        <TabPanel value={activeTab} index={5}>
          <ValidatorsTab agentId={agentId} />
        </TabPanel>
        <TabPanel value={activeTab} index={6}>
          <AgentSchedulePanel agentId={agentId} agentName={agent.name} />
        </TabPanel>
        <TabPanel value={activeTab} index={7}>
          <MemoryTab agent={agent} onEditPolicy={() => setMemoryOpen(true)} />
        </TabPanel>
        <TabPanel value={activeTab} index={8}>
          <ExecutionsTab agentId={agentId} />
        </TabPanel>
        <TabPanel value={activeTab} index={9}>
          <AdvancedTab
            agent={agent}
            onDelete={() => setDeleteOpen(true)}
            canDelete={agent.can_edit !== false}
          />
        </TabPanel>
      </Paper>

      <AgentEditDialog
        open={editOpen}
        agent={agent}
        isStaff={isStaff}
        saving={updateMutation.isPending}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEditSubmit}
      />

      <MemoryConfigDialog
        open={memoryOpen}
        agent={agent}
        saving={updateMutation.isPending}
        onClose={() => setMemoryOpen(false)}
        onSubmit={handleMemorySubmit}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete agent"
        message={`Are you sure you want to delete "${agent.name}"? This will also delete all executions and memories. This action cannot be undone.`}
        onConfirm={() => {
          setDeleteOpen(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setDeleteOpen(false)}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default AgentDetail;
